import getMediaElement from 'editor/src/store/design/selector/getMediaElement';
import { ElementAddress, Rect } from 'editor/src/store/design/types';
import type { Thunk } from 'editor/src/store/hooks';

import { getRectangleBoundingBox } from 'editor/src/util/reflectDesignData/utils';

import alignMediaToSpreadBottomOperation from './alignMediaToSpreadBottomOperation';
import alignMediaToSpreadCenterOperation from './alignMediaToSpreadCenterOperation';
import alignMediaToSpreadLeftOperation from './alignMediaToSpreadLeftOperation';
import alignMediaToSpreadMiddleOperation from './alignMediaToSpreadMiddleOperation';
import alignMediaToSpreadRightOperation from './alignMediaToSpreadRightOperation';
import alignMediaToSpreadTopOperation from './alignMediaToSpreadTopOperation';
import getFocusBox from './getFocusBox';

const operations = {
  left: alignMediaToSpreadLeftOperation,
  center: alignMediaToSpreadCenterOperation,
  right: alignMediaToSpreadRightOperation,
  top: alignMediaToSpreadTopOperation,
  middle: alignMediaToSpreadMiddleOperation,
  bottom: alignMediaToSpreadBottomOperation,
};
export type AlignmentDirection = keyof typeof operations;
export type AlignmentReference = 'selection' | 'page';

const alignSelectedMediaOperation =
  (addresses: ElementAddress[], direction: AlignmentDirection, reference: AlignmentReference = 'selection'): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const selection = addresses.flatMap((address) => {
      const element = getMediaElement(state, address);
      const page = state.design.designData?.spreads[address.spreadIndex]?.pages[address.pageIndex];
      if (!element || !page || element.locked) {
        return [];
      }
      const box =
        element.type === 'line'
          ? {
              x: Math.min(element.x1, element.x2),
              y: Math.min(element.y1, element.y2),
              width: Math.abs(element.x2 - element.x1),
              height: Math.abs(element.y2 - element.y1) + element.strokeWidth,
            }
          : getRectangleBoundingBox(element.x, element.y, element.width, element.height, element.r);
      return [{ address, page, box }];
    });

    // Snapshot each spread's reference before dispatching any position updates.
    const references = new Map<number, Rect>();
    if (reference === 'selection' && selection.length > 1) {
      selection.forEach(({ address, page, box }) => {
        const x = box.x + (page.x || 0);
        const y = box.y + (page.y || 0);
        const previous = references.get(address.spreadIndex);
        const left = Math.min(previous?.x ?? x, x);
        const top = Math.min(previous?.y ?? y, y);
        references.set(address.spreadIndex, {
          x: left,
          y: top,
          width: Math.max(previous ? previous.x + previous.width : x + box.width, x + box.width) - left,
          height: Math.max(previous ? previous.y + previous.height : y + box.height, y + box.height) - top,
        });
      });
    }
    selection.forEach(({ address, page }) => {
      const box =
        reference === 'page' && selection.length > 1
          ? getFocusBox(state, address.spreadIndex)
          : references.get(address.spreadIndex);
      const localReference = box ? { ...box, x: box.x - (page.x || 0), y: box.y - (page.y || 0) } : undefined;
      dispatch(operations[direction](address, localReference));
    });
  };

export default alignSelectedMediaOperation;
